import { Rarity, Tag, type ModDef } from '../../sim/mods';
import { Stat } from '../../sim/stats';

/* ═══════════════════════════════════════════════════════════════════════════
   Les FUSIONS.

   Elles ne se tirent jamais au hasard : elles se DÉBLOQUENT en réunissant deux
   tags à trois exemplaires chacun. Leur poids de tirage passe alors de 2 à 35 —
   le moment « le jeu récompense mon build » doit être fréquent et évident, pas
   une loterie qu'on rate.

   ⚠️ Règle absolue du plan (§6.7) : une Fusion doit CHANGER LA FAÇON DE JOUER,
   jamais seulement les chiffres. Si elle se résume à « +80 % de dégâts », c'est
   un Épique mal étiqueté — et le joueur qui a passé dix cartes à la construire
   se sent floué.

   Les trois premières changent respectivement : le volume de feu, la portée du
   frôlement, et la cadence à un point où le pilotage change de nature.

   ⚠️ POURQUOI HUIT, et pourquoi huit PAIRES DISTINCTES (J12). Avec trois
   fusions, le banc mesurait 14 % de runs qui en décrochaient une, et le
   déblocage tombait au niveau 15 sur 20 — autrement dit la récompense arrivait
   trop tard pour être jouée. La cause n'était pas le poids de tirage mais la
   COUVERTURE : trois paires sur les quinze possibles, donc un build qui
   n'empilait pas exactement CINÉTIQUE+THERMIQUE ne visait rien.

   Les huit paires retenues couvrent chaque tag deux ou trois fois. Conséquence
   directe : quelle que soit la voie suivie, une fusion au moins est en ligne de
   mire — et le compteur de tags du HUD redevient une information actionnable au
   lieu d'une décoration. Le taux de déblocage est vérifié par simulation dans
   tests/balance/pool.test.ts, pas estimé à vue.
   ═══════════════════════════════════════════════════════════════════════════ */

const mods: ModDef[] = [
  {
    id: 'tempete-de-fer',
    name: 'Tempête de fer',
    rarity: Rarity.Fusion,
    tags: [Tag.Cinetique, Tag.Thermique],
    requires: [
      [Tag.Cinetique, 3],
      [Tag.Thermique, 3],
    ],
    maxStacks: 1,
    // Deux projectiles d'un coup : la volée passe d'une ligne à un mur, et la
    // façon de viser change réellement.
    modifiers: [
      { stat: Stat.BulletCount, op: 'add', value: 2 },
      { stat: Stat.Damage, op: 'mul', value: 0.12 },
    ],
    text: 'FUSION — +2 drones d’escorte, +12 % de dégâts',
  },
  {
    id: 'singularite',
    name: 'Singularité',
    rarity: Rarity.Fusion,
    tags: [Tag.Plasma, Tag.Orbital],
    requires: [
      [Tag.Plasma, 3],
      [Tag.Orbital, 3],
    ],
    maxStacks: 1,
    // Le rayon de frôlement double presque : le joueur peut jouer AU CONTACT
    // des projectiles, ce qui est une posture entièrement différente.
    modifiers: [
      { stat: Stat.GrazeRadius, op: 'mul', value: 0.65 },
      { stat: Stat.Damage, op: 'mul', value: 0.25 },
      { stat: Stat.CellGain, op: 'mul', value: 0.4 },
    ],
    text: 'FUSION — +65 % de frôlement, +25 % de dégâts, +40 % de cellules',
  },
  {
    id: 'cascade',
    name: 'Cascade',
    rarity: Rarity.Fusion,
    tags: [Tag.Surcharge, Tag.Drone],
    requires: [
      [Tag.Surcharge, 3],
      [Tag.Drone, 3],
    ],
    maxStacks: 1,
    // À cette cadence, le tir devient un flux continu plutôt qu'une succession
    // de volées : on cesse de viser des ennemis pour balayer des zones.
    modifiers: [
      { stat: Stat.FireRate, op: 'mul', value: 0.55 },
      { stat: Stat.BulletCount, op: 'add', value: 1 },
    ],
    text: 'FUSION — +55 % de cadence, +1 drone d’escorte',
  },

  /* ── J12 — les cinq paires manquantes ─────────────────────────────────── */
  {
    id: 'nuee-convergente',
    name: 'Nuée convergente',
    rarity: Rarity.Fusion,
    tags: [Tag.Cinetique, Tag.Drone],
    requires: [
      [Tag.Cinetique, 3],
      [Tag.Drone, 3],
    ],
    maxStacks: 1,
    // MULTIPLICATIF sur la volée, et c'est le seul endroit du jeu où cette
    // mécanique atteint son plein effet : un build CINÉTIQUE+DRONE arrive ici
    // avec six à huit projectiles, que +30 % portent à sept ou dix. L'écran se
    // remplit, on cesse de viser des ennemis pour tenir des couloirs.
    // ⚠️ Volontairement calée sous les +45 % essayés d'abord. Le banc est net :
    // `bulletCount` gouverne la dispersion des runs, et une fusion qui le
    // multiplie n'enrichit que les parties déjà riches. Le rapport p90/p10
    // dépassait alors son garde-fou.
    modifiers: [
      { stat: Stat.BulletCount, op: 'mul', value: 0.3 },
      { stat: Stat.Damage, op: 'mul', value: 0.15 },
    ],
    text: 'FUSION — +30 % de projectiles (arrondi à l’inférieur), +15 % de dégâts',
  },
  {
    id: 'fournaise',
    name: 'Fournaise',
    rarity: Rarity.Fusion,
    tags: [Tag.Plasma, Tag.Thermique],
    requires: [
      [Tag.Plasma, 3],
      [Tag.Thermique, 3],
    ],
    maxStacks: 1,
    // Le tir devient assez large et assez rapide pour qu'on cesse de viser :
    // on balaie. Le changement porte sur la LARGEUR, donc sur le geste, pas sur
    // le chiffre — c'est ce que le plan exige d'une fusion.
    modifiers: [
      { stat: Stat.BulletRadius, op: 'mul', value: 0.6 },
      { stat: Stat.FireRate, op: 'mul', value: 0.22 },
    ],
    text: 'FUSION — +60 % de largeur de tir, +22 % de cadence',
  },
  {
    id: 'ancre-gravitationnelle',
    name: 'Ancre gravitationnelle',
    rarity: Rarity.Fusion,
    tags: [Tag.Orbital, Tag.Surcharge],
    requires: [
      [Tag.Orbital, 3],
      [Tag.Surcharge, 3],
    ],
    maxStacks: 1,
    // Le vaisseau devient une tourelle : à cette vitesse on ne traverse plus
    // l'écran, on choisit sa position à l'avance et on l'assume. C'est la
    // posture la plus éloignée du jeu de base que le pool sache produire, et
    // l'aboutissement franc de la voie du verre.
    modifiers: [
      { stat: Stat.Damage, op: 'mul', value: 0.5 },
      { stat: Stat.MoveSpeed, op: 'mul', value: -0.3 },
    ],
    text: 'FUSION — +50 % de dégâts, −30 % de vitesse de déplacement',
  },
  {
    id: 'lance-de-lumiere',
    name: 'Lance de lumière',
    rarity: Rarity.Fusion,
    tags: [Tag.Cinetique, Tag.Plasma],
    requires: [
      [Tag.Cinetique, 3],
      [Tag.Plasma, 3],
    ],
    maxStacks: 1,
    // L'exact opposé de « Fournaise » : un trait fin, très rapide, très lourd.
    // Le pilote doit viser, ce qu'aucune autre fusion ne demande — et c'est
    // pour ça que les deux peuvent coexister dans le même pool sans que l'une
    // rende l'autre inutile.
    modifiers: [
      { stat: Stat.Damage, op: 'mul', value: 0.4 },
      { stat: Stat.BulletSpeed, op: 'mul', value: 0.5 },
      { stat: Stat.BulletRadius, op: 'mul', value: -0.35 },
    ],
    text: 'FUSION — +40 % de dégâts, +50 % de vitesse de tir, −35 % de largeur de tir',
  },
  {
    id: 'rideau-incendiaire',
    name: 'Rideau incendiaire',
    rarity: Rarity.Fusion,
    tags: [Tag.Thermique, Tag.Drone],
    requires: [
      [Tag.Thermique, 3],
      [Tag.Drone, 3],
    ],
    maxStacks: 1,
    // Des projectiles gros et lents, tirés très vite : le tir cesse d'être une
    // ligne et devient un mur qui monte. Il faut anticiper la trajectoire des
    // ennemis au lieu de les suivre.
    modifiers: [
      { stat: Stat.FireRate, op: 'mul', value: 0.35 },
      { stat: Stat.BulletRadius, op: 'mul', value: 0.45 },
      { stat: Stat.BulletSpeed, op: 'mul', value: -0.3 },
    ],
    text: 'FUSION — +35 % de cadence, +45 % de largeur de tir, −30 % de vitesse de tir',
  },
];

export default mods;
